import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ImageModule } from '@df/module-image/image.module';
import { RatioOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';

export namespace NPageComponentVideo {
    export interface IContentData {
        ratio: string;
        video_id: string;
        video_provider: 'youtube' | 'vimeo';
        video_autoplay: boolean;
        video_background: boolean;
        video_hide: boolean;
        background_image?: string;
    }
}

@Component({
    selector: 'page-component-video',
    templateUrl: './video.html',
    styleUrls: ['video.scss'],
    standalone: true,
    imports: [NgClass, NgIf, ImageModule, YoutubePlayerComponent, VimeoPlayerComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
// This decorator can work only in Ivy
@ICmsComponent<PageComponentVideoComponent>({
    id: 'video',
    name: 'Video',
    image: '/common/cms/video-component.png',
    tags: ['video', 'image', 'big', 'banner'],
    isAdvanced: true,
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-3-1'
        },
        // video
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-positioning',
            title: 'Video'
        },
        {
            key: 'video_id',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Video ID or URL',
            default: '867203570'
        },
        <NICmsContentField.IChoiceField>{
            key: 'video_provider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Provider',
            options: [
                ...UndefinedOption,
                {
                    value: 'youtube',
                    title: 'YouTube'
                },
                {
                    value: 'vimeo',
                    title: 'Vimeo'
                }
            ],
            description: 'Choose a video provider',
            default: 'vimeo'
        },
        {
            key: 'video_background',
            type: NICmsContentField.EFieldType.checkbox,
            title: 'Background Mode',
            default: true
        },
        {
            key: 'video_autoplay',
            type: NICmsContentField.EFieldType.checkbox,
            title: 'Autoplay',
            default: true,
            canShow: (data: NPageComponentVideo.IContentData) => !data.video_background
        },
        {
            key: 'video_hide',
            type: NICmsContentField.EFieldType.checkbox,
            title: 'Hide',
            default: false
        },
        // background
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-background',
            title: 'Background'
        },
        {
            key: 'background_image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            default: undefined
        }
    ]
})
export class PageComponentVideoComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component: NPageComponentVideo.IContentData;
}
